(() => {
  if (window.SKULL_SLOT_GATE) return;
  const config = window.SKULL_SLOT_CONFIG || {};
  const root = document.documentElement;
  const storageKey = 'skull-slot-unlocked-v1';
  const badgeSelector = '.skull-live-shop [class^="skull-pvg-badge-"]';
  const promoSelector = '.skull-live-shop .pvg-rich-subheading,.pvg-refined-offer,#campaign .campaign-flash-marquee,.sc-heading > p,.skull-pclean,[class^="ai-urgency-text-"]';
  const purchaseSelector = '[data-testid="cart-drawer-trigger"],.open-cart,.header-actions__action.action__cart,[class^="skull-pvg-add-card-"],.pvg-refined-sheet button,.sc-models button,.sc-colors button,.sc-sizes button,.sc-purchase button,.sc-purchase input,button[name="add"],button[name="checkout"],a[href="/cart"],a[href^="/checkout"],a[href*="/products/"]';
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const lifecycle = new AbortController();
  const timers = new Set();
  const pending = new Set();
  const animations = new Set();
  const seenBadges = new WeakSet();
  const seenPromos = new WeakSet();
  const cardTargets = new Map();
  let unlocked = root.dataset.skullPromo !== 'locked';
  let revealEnabled = false;
  let started = false;
  let disposed = false;
  let suspended = false;
  let bootstrapComplete = false;
  let idleId = 0;
  let intersection;
  let cardIntersection;

  function later(callback, delay) {
    const timer = setTimeout(() => { timers.delete(timer); callback(); }, delay);
    timers.add(timer);
    return timer;
  }

  function cancelLoading() {
    for (const timer of timers) clearTimeout(timer);
    timers.clear();
    if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
    idleId = 0;
    clearTimeout(window.SKULL_SLOT_FAILSAFE);
  }

  function trackAnimation(animation) {
    animations.add(animation);
    animation.finished.catch(() => {}).finally(() => animations.delete(animation));
  }

  function reveal(element, delay = 0) {
    intersection?.unobserve(element);
    pending.delete(element);
    element.removeAttribute('data-skull-promo-pending');
    if (disposed || reducedMotion.matches || !element.isConnected || !element.animate) return;
    // The hero and countdown retain their own opacity/display rules.
    trackAnimation(element.animate([
      { opacity: 0, translate: '0 4px' },
      { opacity: 1, translate: '0 0' }
    ], { duration: 360, delay, easing: 'cubic-bezier(.2,.75,.25,1)', fill: 'backwards' }));
  }

  function plateArrives(badge, delay = 0) {
    badge.removeAttribute('data-skull-plate-pending');
    badge.dataset.skullPlateRevealed = 'true';
    if (disposed || reducedMotion.matches || !badge.isConnected || !badge.animate) return;
    // One short placement/settle, with a highlight over the existing red artwork.
    trackAnimation(badge.animate([
      { opacity: 0, transform: 'perspective(420px) translate3d(-5px,-11px,30px) rotateY(-25deg) rotateZ(-7deg) scale(1.16)', offset: 0 },
      { opacity: 1, transform: 'perspective(420px) translate3d(0,0,0) rotateY(0deg) rotateZ(.8deg) scale(.975)', offset: .57, easing: 'ease-out' },
      { opacity: 1, transform: 'perspective(420px) translate3d(0,-1px,0) rotateY(0deg) rotateZ(-.35deg) scale(1.012)', offset: .75, easing: 'ease-in-out' },
      { opacity: 1, transform: 'perspective(420px) translate3d(0,0,0) rotateY(0deg) rotateZ(0deg) scale(1)', offset: 1 }
    ], { duration: 620, delay, easing: 'cubic-bezier(.18,.78,.28,1)', fill: 'backwards' }));
    const artwork = badge.querySelector('img');
    if (artwork?.animate) trackAnimation(artwork.animate([
      { filter: 'brightness(1.12) drop-shadow(0 7px 5px rgb(0 0 0 / .28))', offset: 0 },
      { filter: 'brightness(1.24) drop-shadow(0 1px 1px rgb(0 0 0 / .2))', offset: .57 },
      { filter: 'brightness(1) drop-shadow(0 1px 1px rgb(0 0 0 / .18))', offset: 1 }
    ], { duration: 620, delay, easing: 'ease-out', fill: 'backwards' }));
  }

  function revealCard(target, delay = 0) {
    const state = cardTargets.get(target);
    if (!state || !state.visible || !unlocked || suspended || document.querySelector('#skull-slot-popup[open]')) return;
    for (const badge of state.badges) plateArrives(badge, delay);
    cardTargets.delete(target);
    cardIntersection?.unobserve(target);
  }

  function elementsWithin(container, selector) {
    if (!(container instanceof Element || container instanceof Document)) return [];
    return [...(container instanceof Element && container.matches(selector) ? [container] : []), ...container.querySelectorAll(selector)];
  }

  function visibleEnough(target) {
    const box = target.getBoundingClientRect();
    const height = Math.max(0, Math.min(box.bottom, innerHeight - 144) - Math.max(box.top, 0));
    const width = Math.max(0, Math.min(box.right, innerWidth) - Math.max(box.left, 0));
    return box.width > 0 && box.height > 0 && (height * width) / (box.width * box.height) >= .28;
  }

  function markBadges(container) {
    if (disposed || suspended || !cardIntersection) return;
    for (const badge of elementsWithin(container, badgeSelector)) {
      if (seenBadges.has(badge)) continue;
      seenBadges.add(badge);
      badge.removeAttribute('data-skull-plate-revealed');
      const target = badge.closest('[class^="skull-pvg-media-"]') || badge.closest('[data-pvg-card]') || badge.parentElement;
      if (!target) continue;
      // A delayed script must never hide an already displayed badge and blink it back.
      if (!bootstrapComplete && config.badgeBootstrapExpired && unlocked && visibleEnough(target)) {
        badge.dataset.skullPlateRevealed = 'true';
        badge.removeAttribute('data-skull-plate-pending');
        continue;
      }
      badge.setAttribute('data-skull-plate', '');
      badge.setAttribute('data-skull-plate-pending', '');
      let state = cardTargets.get(target);
      if (!state) {
        state = { badges: new Set(), visible: false };
        cardTargets.set(target, state);
        cardIntersection.observe(target);
      }
      state.badges.add(badge);
    }
  }

  function markPromos(container) {
    if (!revealEnabled || disposed || suspended) return;
    for (const element of elementsWithin(container, promoSelector)) {
      if (seenPromos.has(element)) continue;
      seenPromos.add(element);
      element.setAttribute('data-skull-promo-pending', '');
      pending.add(element);
      intersection.observe(element);
    }
  }

  function showAllPending() {
    intersection?.disconnect();
    cardIntersection?.disconnect();
    for (const element of pending) element.removeAttribute('data-skull-promo-pending');
    pending.clear();
    for (const state of cardTargets.values()) for (const badge of state.badges) badge.removeAttribute('data-skull-plate-pending');
    cardTargets.clear();
    for (const animation of animations) animation.cancel();
    animations.clear();
  }

  function unlock(source = 'close', animate = true) {
    cancelLoading();
    if (unlocked) return;
    unlocked = true;
    try { sessionStorage.setItem(storageKey, '1'); } catch (_) {}
    revealEnabled = animate && !reducedMotion.matches && 'IntersectionObserver' in window;
    if (revealEnabled) {
      intersection = new IntersectionObserver(entries => {
        let stagger = 0;
        for (const entry of entries) if (entry.isIntersecting) reveal(entry.target, Math.min(stagger++ * 45, 180));
      }, { rootMargin: '0px 0px -8px 0px', threshold: .04 });
      markPromos(document);
    }
    root.removeAttribute('data-skull-promo');
    let stagger = 0;
    for (const [target, state] of cardTargets) if (state.visible) revealCard(target, Math.min(stagger++ * 65, 195));
    document.dispatchEvent(new CustomEvent('skull:promo-unlocked', { detail: { source } }));
  }

  window.SKULL_SLOT_GATE = { isUnlocked: () => unlocked, unlock };

  function isPopupTarget(target) {
    return target instanceof Element && !!target.closest('#skull-slot-popup');
  }

  function otherModalIsOpen() {
    return !!document.querySelector('dialog[open]:not(#skull-slot-popup),.pvg-refined-modal.active,[class^="skull-pvg-modal-"].active');
  }

  function customerIsTyping() {
    const active = document.activeElement;
    return active instanceof Element && !isPopupTarget(active) && active.matches('input,textarea,select,[contenteditable="true"]');
  }

  function shouldYield() {
    return document.hidden || otherModalIsOpen() || customerIsTyping() || (window.__SKULL_CART?.item_count || 0) > 0;
  }

  const gridChanges = new MutationObserver(records => {
    if (disposed || suspended) return;
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element) || isPopupTarget(node)) continue;
        markBadges(node);
        markPromos(node);
      }
    }
    // Section replacement and filters must not retain removed card DOM.
    for (const [target, state] of cardTargets) {
      for (const badge of state.badges) if (!badge.isConnected) state.badges.delete(badge);
      if (!target.isConnected || !state.badges.size) {
        cardIntersection?.unobserve(target);
        cardTargets.delete(target);
      }
    }
    for (const element of pending) if (!element.isConnected) { intersection?.unobserve(element); pending.delete(element); }
  });
  const modalChanges = new MutationObserver(() => {
    if (!unlocked && otherModalIsOpen()) unlock('purchase-intent', false);
  });

  function observeSections() {
    // MainContent covers replaced grids without watching animation/style mutations.
    gridChanges.disconnect();
    const content = document.querySelector('#MainContent') || document.querySelector('main');
    if (content) gridChanges.observe(content, { childList: true, subtree: true });
    for (const grid of document.querySelectorAll('#catalog-grid,.product-grid')) {
      if (!content?.contains(grid)) gridChanges.observe(grid, { childList: true, subtree: true });
    }
    modalChanges.disconnect();
    for (const modal of document.querySelectorAll('dialog:not(#skull-slot-popup),.pvg-refined-modal')) {
      modalChanges.observe(modal, { attributes: true, attributeFilter: ['open', 'class'] });
    }
  }

  if ('IntersectionObserver' in window && config.catalog !== false) {
    cardIntersection = new IntersectionObserver(entries => {
      let stagger = 0;
      for (const entry of entries) {
        const state = cardTargets.get(entry.target);
        if (!state) continue;
        state.visible = entry.isIntersecting && entry.intersectionRatio >= .28;
        if (state.visible) revealCard(entry.target, Math.min(stagger++ * 65, 195));
      }
    }, { rootMargin: '0px 0px -144px 0px', threshold: [0, .28] });
    markBadges(document);
  }
  bootstrapComplete = true;
  clearTimeout(window.SKULL_SLOT_BADGE_FAILSAFE);
  root.removeAttribute('data-skull-badge-boot');

  document.addEventListener('click', event => {
    if (unlocked || isPopupTarget(event.target) || !(event.target instanceof Element)) return;
    if (event.target.closest(purchaseSelector)) unlock('purchase-intent', false);
  }, { capture: true, signal: lifecycle.signal });
  document.addEventListener('focusin', event => {
    if (!unlocked && !isPopupTarget(event.target) && customerIsTyping()) unlock('form-intent', false);
  }, { signal: lifecycle.signal });
  document.addEventListener('skull:cart-synced', event => {
    if (!unlocked && event.detail?.cart?.item_count > 0) unlock('cart', false);
  }, { signal: lifecycle.signal });
  document.addEventListener('shopify:section:load', event => {
    observeSections();
    markBadges(event.target);
    markPromos(event.target);
  }, { signal: lifecycle.signal });
  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;
    revealEnabled = false;
    for (const element of [...pending]) reveal(element);
    for (const animation of animations) animation.cancel();
    animations.clear();
  }, { signal: lifecycle.signal });
  observeSections();

  function loadStylesheet() {
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = config.cssUrl;
      link.id = 'skull-slot-popup-styles';
      link.onload = () => { link.onload = null; link.onerror = null; resolve(); };
      link.onerror = () => { link.remove(); reject(new Error('Popup stylesheet unavailable')); };
      document.head.append(link);
    });
  }

  async function loadAndOpen() {
    if (started || unlocked || disposed || suspended) return;
    started = true;
    if (shouldYield()) { unlock('visitor-busy', false); return; }
    try {
      const [, popup] = await Promise.all([loadStylesheet(), import(config.moduleUrl)]);
      if (unlocked || disposed || suspended) return;
      if (shouldYield()) { unlock('visitor-busy', false); return; }
      const opened = await popup.openPopup();
      if (opened) clearTimeout(window.SKULL_SLOT_FAILSAFE);
      else unlock('popup-skipped', false);
    } catch (_) {
      unlock('load-error', false);
    }
  }

  function schedule() {
    if (unlocked || disposed || suspended || started || idleId || !config.eligible) return;
    const begin = () => {
      idleId = 0;
      const remaining = Math.max(0, (Number(config.delayMs) || 3000) - performance.now());
      later(loadAndOpen, remaining);
    };
    if ('requestIdleCallback' in window) idleId = requestIdleCallback(begin, { timeout: 700 });
    else later(begin, 50);
  }

  if (!config.eligible || unlocked) {
    root.removeAttribute('data-skull-promo');
    cancelLoading();
  } else {
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true, signal: lifecycle.signal });
    later(schedule, 4000);
  }

  window.addEventListener('pagehide', event => {
    suspended = true;
    cancelLoading();
    for (const animation of animations) animation.cancel();
    animations.clear();
    gridChanges.disconnect();
    modalChanges.disconnect();
    cardIntersection?.disconnect();
    intersection?.disconnect();
    for (const state of cardTargets.values()) state.visible = false;
    if (!event.persisted) {
      disposed = true;
      showAllPending();
      lifecycle.abort();
    }
  }, { signal: lifecycle.signal });
  window.addEventListener('pageshow', event => {
    if (!event.persisted || disposed) return;
    suspended = false;
    if (!unlocked) unlock('back-forward', false);
    observeSections();
    markBadges(document);
    for (const target of cardTargets.keys()) cardIntersection?.observe(target);
    for (const element of pending) intersection?.observe(element);
  }, { signal: lifecycle.signal });
})();
